import { useCallback, useEffect, useState, useMemo } from "react";
import { api, idr, idrShort } from "../../lib/api";
import { toast } from "sonner";
import { Target, Plus, TrendingUp, AlertTriangle, Send, X, Trash2 } from "lucide-react";
import InputRupiah from "../../components/InputRupiah";
import { MonthPicker } from "../../components/ui/date-picker";
import LoadingState from "../../components/ui/loading-state";
import { bulanIniISO } from "../../lib/tanggal";

const ALERT_STYLE = {
  hijau:  { c: "bg-emerald-50 border-emerald-200 text-emerald-800", bar: "bg-emerald-500", label: "Aman" },
  kuning: { c: "bg-amber-50 border-amber-200 text-amber-800",       bar: "bg-amber-500",   label: "≥80%" },
  merah:  { c: "bg-rose-50 border-rose-200 text-rose-800",           bar: "bg-rose-500",    label: "OVER" },
  belum_budget: { c: "bg-slate-50 border-slate-200 text-slate-600",  bar: "bg-slate-400",   label: "No Budget" },
};

export default function FinanceBudget() {
  const [periode, setPeriode] = useState(bulanIniISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [bebanAccs, setBebanAccs] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try { const r = await api.get(`/finance/budget/actual?periode=${periode}`); setData(r.data); }
    catch (e) { setLoadError(e); toast.error("Gagal load budget"); }
    finally { setLoading(false); }
  }, [periode]);
  useEffect(() => { load(); api.get("/finance/coa").then(r => setBebanAccs((r.data || []).filter(a => a.category === "beban"))); }, [load]);

  const trigger = async () => {
    if (!confirm(`Kirim budget alert bulan ${periode} ke Telegram?`)) return;
    setBusy(true);
    try { await api.post("/finance/budget/trigger-alert"); toast.success("Alert dikirim ke Telegram"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!confirm("Hapus budget ini?")) return;
    try { await api.delete(`/finance/budget/${id}`); toast.success("Budget dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const critsCount = data?.rows.filter(r => r.alert === "merah").length || 0;
  const warnsCount = data?.rows.filter(r => r.alert === "kuning").length || 0;

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-budget">
      <div className="flex items-start justify-between flex-col md:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Budget vs Actual</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Anggaran Beban</h1>
          <p className="text-sm text-slate-600 mt-1">Set budget bulanan per kategori · alert 80% & 100% ke Telegram</p>
        </div>
        <div className="flex gap-2">
          <MonthPicker value={periode} onChange={setPeriode} data-testid="budget-periode" className="w-36" />
          <button onClick={trigger} disabled={busy} data-testid="budget-trigger-alert" className="h-10 px-4 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-2 disabled:opacity-50"><Send className="w-4 h-4" /> Kirim Alert</button>
          <button onClick={() => setShowForm(true)} data-testid="budget-new" className="h-10 px-4 rounded-lg bg-amber-600 text-white hover:bg-amber-700 text-sm font-medium inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Set Budget</button>
        </div>
      </div>

      <LoadingState loading={loading} error={loadError} onRetry={load} varian="kartu" jumlahKartu={4} data-testid="loading-budget">
      {data && <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total Budget</p>
          <p className="font-display text-xl font-bold text-slate-900 mt-1">{idrShort(data.total_budget)}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total Actual</p>
          <p className="font-display text-xl font-bold text-amber-700 mt-1">{idrShort(data.total_actual)}</p>
          <p className="text-[11px] text-slate-500">{data.total_pct}% terpakai</p>
        </div>
        <div className={critsCount > 0 ? "bg-rose-50 border-rose-200" : "bg-white border-slate-200"}>
          <div className={`border rounded-xl p-4 shadow-sm ${critsCount > 0 ? "bg-rose-50 border-rose-200" : "bg-white border-slate-200"}`}>
            <p className={`text-[10px] font-semibold uppercase tracking-wider ${critsCount > 0 ? "text-rose-700" : "text-slate-500"}`}>Over Budget</p>
            <p className={`font-display text-xl font-bold mt-1 ${critsCount > 0 ? "text-rose-800" : "text-slate-900"}`}>{critsCount} kategori</p>
          </div>
        </div>
        <div className={`border rounded-xl p-4 shadow-sm ${warnsCount > 0 ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"}`}>
          <p className={`text-[10px] font-semibold uppercase tracking-wider ${warnsCount > 0 ? "text-amber-700" : "text-slate-500"}`}>Mendekati Batas</p>
          <p className={`font-display text-xl font-bold mt-1 ${warnsCount > 0 ? "text-amber-800" : "text-slate-900"}`}>{warnsCount} kategori</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100">
          <p className="font-display font-bold text-base text-slate-900">Detail Kategori</p>
          <p className="text-xs text-slate-500 mt-0.5">Diurut: over budget → mendekati batas → sesuai</p>
        </div>
        <div className="p-3 space-y-2">
          {data.rows.length === 0 ? <div className="p-10 text-center text-sm text-slate-400">Belum ada budget atau beban di periode ini</div> :
            data.rows.map(r => {
              const st = ALERT_STYLE[r.alert] || ALERT_STYLE.hijau;
              const pct = r.pct_used === null ? 0 : Math.min(r.pct_used, 200);
              return (
                <div key={r.akun_code} data-testid={`budget-row-${r.akun_code}`} className={`p-3 rounded-lg border ${st.c}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900">{r.akun_name}</p>
                        <span className="text-[10px] font-mono text-slate-400">{r.akun_code}</span>
                        {r.alert === "merah" && <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-rose-700"><AlertTriangle className="w-3 h-3" /> OVER</span>}
                        {r.alert === "belum_budget" && <span className="text-[9px] font-semibold uppercase text-slate-500">no budget</span>}
                      </div>
                      <p className="text-xs text-slate-600 mt-0.5">Budget <b>{idrShort(r.budget)}</b> · Actual <b>{idrShort(r.actual)}</b>{r.budget > 0 && ` · Sisa ${idrShort(r.sisa)}`}</p>
                    </div>
                    <div className="text-right">
                      {r.pct_used !== null && <p className={`font-mono font-bold ${r.alert === "merah" ? "text-rose-700" : r.alert === "kuning" ? "text-amber-700" : "text-emerald-700"}`}>{r.pct_used}%</p>}
                      {r.budget_id && <button onClick={() => del(r.budget_id)} className="text-slate-400 hover:text-rose-600 text-xs mt-1"><Trash2 className="w-3 h-3" /></button>}
                    </div>
                  </div>
                  {r.budget > 0 && (
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${st.bar} transition-all`} style={{ width: `${pct}%` }} />
                    </div>
                  )}
                </div>
              );
            })
          }
        </div>
      </div>

      {showForm && <FormBudget periode={periode} bebanAccs={bebanAccs} existingBudgets={data.rows.filter(r => r.budget_id).map(r => r.akun_id)} onClose={() => { setShowForm(false); load(); }} />}
      </>}
      </LoadingState>
    </div>
  );
}

function FormBudget({ periode, bebanAccs, existingBudgets, onClose }) {
  const [f, setF] = useState({ periode, akun_id: "", jumlah: "", catatan: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { const r = await api.post("/finance/budget", { ...f, jumlah: Number(f.jumlah) }); toast.success(r.data.message); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };
  const available = bebanAccs.filter(a => !existingBudgets.includes(a.id));
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <h3 className="font-display font-bold text-lg mb-4">Set Budget · {periode}</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Kategori Beban</label>
            <select value={f.akun_id} onChange={e => setF({ ...f, akun_id: e.target.value })} className="in" data-testid="budget-form-akun"><option value="">Pilih...</option>{available.map(a => <option key={a.id} value={a.id}>{a.name} ({a.code})</option>)}</select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Jumlah Budget</label>
            <InputRupiah value={f.jumlah} onChange={n => setF({ ...f, jumlah: n })} className="in" testid="budget-form-jumlah" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Catatan</label>
            <input value={f.catatan} onChange={e => setF({ ...f, catatan: e.target.value })} className="in" />
          </div>
        </div>
        <div className="flex gap-2 mt-5"><button onClick={onClose} className="flex-1 h-10 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm">Batal</button><button onClick={submit} disabled={busy || !f.akun_id || !f.jumlah} data-testid="budget-form-submit" className="flex-1 h-10 rounded-lg bg-amber-600 text-white hover:bg-amber-700 text-sm font-medium disabled:opacity-50">{busy ? "..." : "Simpan"}</button></div>
        <style>{`.in { width: 100%; height: 40px; padding: 0 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; } .in:focus { border-color: #D97706; }`}</style>
      </div>
    </div>
  );
}
